import { TranslationPrefetchService } from './core/loaders/translation-prefetch.service'
import { demoSession } from './demo/demo-session'
import { demoInterceptor } from './demo/demo.interceptor'
import { registerLocaleData } from '@angular/common'
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http'
import localeAr from '@angular/common/locales/ar'
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { provideServiceWorker } from '@angular/service-worker'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { ActivatedRouteSnapshot, provideRouter, Router, withViewTransitions } from '@angular/router'
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core'
import { MultiFileTranslateLoader } from './core/loaders/multi-file-translate-loader'
import Aura from '@primeng/themes/aura'
import * as Sentry from '@sentry/angular'
// Not the library barrel: see ui-components/src/lib/tokens.ts.
import { ENVIRONMENT_TOKEN, FOLDERS_SERVICE, ORDERS_SERVICE_TOKEN } from '@take-theme/ui-components/tokens'
import { provideToastr } from 'ngx-toastr'
import { providePrimeNG } from 'primeng/config'
import { environment } from '../environments/environment'
import { appRoutes } from './app.routes'
import { AppUpdateService } from './core/pwa/app-update.service'
import { offlineQueueInterceptor } from './core/pwa/offline-queue.interceptor'
import { GlobalErrorHandler } from './shared/handlers/global-error.handler'
import { errorTranslationInterceptor } from './shared/interceptor/errorTranslation.interceptor'
import { jwtInterceptor } from './shared/interceptor/jwt.interceptor'
import { rateLimitInterceptor } from './shared/interceptor/rateLimit.interceptor'
import { refreshTokenInterceptor } from './shared/interceptor/refreshToken.interceptor'
import { FlagsService } from './shared/services/flags.service'
import { FoldersService } from './shared/services/folders.service'
import { OrdersService } from './shared/services/orders.service'
import { ProductAnalyticsService } from './shared/services/product-analytics.service'
import { SharedService } from './shared/services/shared.service'
import { ViewTransitionService } from './shared/services/view-transition.service'
import { firstValueFrom } from 'rxjs'

export function MultiFileLoaderFactory(http: HttpClient) {
  return new MultiFileTranslateLoader(http, './assets/i18n')
}

// Angular only ships en-US locale data by default; without this every
// date/number pipe renders en-US even when the app language is Arabic.
registerLocaleData(localeAr)

function getStoredLanguage(): string {
  if (demoSession()) return demoSession()!.profile.user.viewLanguage || 'ar'
  try {
    return JSON.parse(localStorage.getItem('language') || '"ar"')
  } catch {
    return 'ar'
  }
}

// Only 'ar' locale data is registered (en-US is built in). Other stored
// languages (fr/du/ge) fall back to en-US so pipes never hit an
// unregistered locale. Language switches reload the page, so resolving
// once at bootstrap is safe.
function resolveLocaleId(): string {
  return getStoredLanguage() === 'ar' ? 'ar' : 'en-US'
}

/**
 * Full path of a navigation, ignoring query params and fragment. `from`/`to`
 * are the root snapshots, so walk the primary-outlet chain down to the leaf.
 */
function snapshotPath(root: ActivatedRouteSnapshot): string {
  const segments: string[] = []
  for (let node: ActivatedRouteSnapshot | null = root; node; node = node.firstChild) {
    for (const segment of node.url) segments.push(segment.path)
  }
  return '/' + segments.join('/')
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => inject(TranslationPrefetchService).start()),
    {
      provide: LOCALE_ID,
      useFactory: resolveLocaleId,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withViewTransitions({
        skipInitialTransition: true,
        // A navigation that only rewrites query params — settings tabs, list
        // filters, pagination — is movement *inside* one page, not a new
        // destination. Running the page-level transition on it cross-fades the
        // entire shell, side menu included, so a tab click reads as the whole
        // page reloading. Skipped here; pages animate their own content.
        onViewTransitionCreated: ({ transition, from, to }) => {
          if (snapshotPath(from) === snapshotPath(to)) {
            transition.skipTransition()
            return
          }
          // Published so arrival animations can queue behind the page move
          // instead of playing over it — see ViewTransitionService.
          inject(ViewTransitionService).track(transition)
        },
      }),
    ),
    // offlineQueueInterceptor is outermost: offline edits are queued before
    // auth headers are added, and replayed through the full chain later.
    provideHttpClient(
      withInterceptors([
        demoInterceptor,
        offlineQueueInterceptor,
        jwtInterceptor,
        errorTranslationInterceptor,
        refreshTokenInterceptor,
        rateLimitInterceptor,
      ]),
    ),
    // PWA: app shell + API read cache (ngsw-config.json). Off in `nx serve` so
    // local development never runs against a cached build.
    // Registered right away (not when stable) so every open checks for a new build at once;
    // AppUpdateService starts at bootstrap, before login, and applies a ready build on open.
    provideServiceWorker('ngsw-worker.js', { enabled: !isDevMode(), registrationStrategy: 'registerImmediately' }),
    provideAppInitializer(() => inject(AppUpdateService).start()),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      closeButton: false,
      progressBar: true,
      enableHtml: false,
      tapToDismiss: true,
      toastClass: 'ngx-toastr',
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning',
      },
    }),
    provideTranslateService({
      // Do NOT pass `defaultLanguage` here: it makes the TranslateService
      // CONSTRUCTOR fire the core i18n HTTP load, and our interceptors
      // (errorTranslation → TranslateService, refreshToken → AuthService →
      // SharedService → TranslateService) then inject the service that is
      // mid-construction — a circular-DI throw the loader's catchError turns
      // into a silently EMPTY translation table that never reloads. The
      // APP_INITIALIZER below sets the default language and loads core files
      // after construction, where injection resolves normally.
      loader: {
        provide: TranslateLoader,
        useFactory: MultiFileLoaderFactory,
        deps: [HttpClient],
      },
    }),
    // Load core translations before any route resolves.
    // This prevents the race where a route resolver merges feature
    // translations and then the core loader overwrites them.
    {
      provide: APP_INITIALIZER,
      useFactory: (shared: SharedService) => () => {
        const lang = getStoredLanguage()
        // Never block bootstrap on a failed translation load — the app is
        // usable untranslated, and the loader logs which file failed.
        return firstValueFrom(shared.setLangSettings(lang)).catch(err => {
          console.error('[i18n] core translation load failed at bootstrap', err)
        })
      },
      deps: [SharedService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    ...(environment.production
      ? [
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
    {
      provide: FOLDERS_SERVICE,
      useExisting: FoldersService,
    },
    {
      provide: ORDERS_SERVICE_TOKEN,
      useExisting: OrdersService,
    },
    {
      provide: ENVIRONMENT_TOKEN,
      useValue: environment,
    },
    // Flagsmith starts at bootstrap but is NOT awaited: the factory returns
    // nothing, so no route waits on a round trip to the flag server. Awaiting
    // it put that request in front of first paint on every cold load —
    // /register and /login included, which read a single flag between them.
    // Flag-gated surfaces read `FlagsService.has()` (a signal), so they appear
    // when the flags arrive instead of holding the whole app back.
    {
      provide: APP_INITIALIZER,
      useFactory: (flags: FlagsService) => () => {
        void flags.init()
      },
      deps: [FlagsService],
      multi: true,
    },
    // PostHog: record the landing hit and start mirroring auth state onto the
    // person/group. Synchronous — the snippet in index.html has already put a
    // queueing stub on window.posthog, so nothing here waits on the network.
    {
      provide: APP_INITIALIZER,
      useFactory: (analytics: ProductAnalyticsService) => () => analytics.init(),
      deps: [ProductAnalyticsService],
      multi: true,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        // PrimeNG defaults to system-preference dark mode (media query),
        // independent of the app's own light/dark toggle (body.dark-theme).
        // The two could disagree, leaving PrimeNG components themed
        // opposite to the rest of the app. Key PrimeNG's dark variant off
        // the same class the app toggle already sets (see TT-458).
        options: {
          darkModeSelector: 'body.dark-theme',
        },
      },
    }),
  ],
}
